/**
 * Feature Flags Settings Page
 *
 * Per-user toggles for behavioral and navigation flags. Changes persist in
 * Supabase and follow the user across devices.
 */

import { useState, useEffect } from 'react';
import {
  FlaskConical,
  RefreshCw,
  ShieldAlert,
  X,
  XCircle,
} from 'lucide-react';
import { PageHeader } from '../../../components/prioritization/PageHeader';
import {
  FEATURE_FLAGS,
  FLAG_CONFIGS,
  isFeatureEnabled,
  enableFeature,
  disableFeature,
  resetFeature,
  loadUserFlags,
  areUserFlagsLoaded,
  FEATURE_FLAG_USAGE,
  FEATURE_FLAGS_UPDATED_EVENT,
  type FeatureFlagValue,
} from '../../../lib/featureFlags';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { useIsElevationTeam } from '../../../lib/elevationAccess';
import {
  WORKSPACE_MODULES,
  canUserOverrideModule,
  getAdminForcedReenabledFlags,
  isFlagListedFor,
  isModuleEnabled,
  resolveModuleGate,
  useWorkspaceModules,
  useWorkspaceModulePolicies,
  type AdminForcedReenabledFlag,
} from '../../../lib/workspaceModulesService';
import {
  FLAG_SETTINGS_GROUPS,
  FLAG_SETTINGS_GROUP_ORDER,
  getFlagSettingsGroup,
  type FlagSettingsGroupId,
} from '../../../lib/featureFlagGroups';

function alwaysOnDismissStorageKey(
  workspaceId: string,
  userId: string,
  moduleKey: string,
): string {
  return `module-always-on-dismiss:${workspaceId}:${userId}:${moduleKey}`;
}

function isAlwaysOnNoticeDismissed(
  workspaceId: string,
  userId: string,
  moduleKey: string,
): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(alwaysOnDismissStorageKey(workspaceId, userId, moduleKey)) === '1';
  } catch {
    return false;
  }
}

function dismissAlwaysOnNotice(
  workspaceId: string,
  userId: string,
  moduleKey: string,
): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(alwaysOnDismissStorageKey(workspaceId, userId, moduleKey), '1');
  } catch {
    // ignore quota / private mode
  }
}

function groupFlags(flagValues: FeatureFlagValue[]): Partial<Record<FlagSettingsGroupId, FeatureFlagValue[]>> {
  const grouped: Partial<Record<FlagSettingsGroupId, FeatureFlagValue[]>> = {};
  for (const flagValue of flagValues) {
    const group = getFlagSettingsGroup(flagValue);
    if (!grouped[group]) grouped[group] = [];
    grouped[group]!.push(flagValue);
  }
  for (const group of FLAG_SETTINGS_GROUP_ORDER) {
    grouped[group]?.sort((a, b) => {
      const nameA = FLAG_CONFIGS[a]?.name ?? a;
      const nameB = FLAG_CONFIGS[b]?.name ?? b;
      return nameA.localeCompare(nameB);
    });
  }
  return grouped;
}

interface FlagGroupSectionProps {
  groupId: FlagSettingsGroupId;
  flagValues: FeatureFlagValue[];
  flags: Record<string, boolean>;
  onToggle: (keyName: string) => void;
  onReset: (keyName: string) => void;
}

function FlagGroupSection({ groupId, flagValues, flags, onToggle, onReset }: FlagGroupSectionProps) {
  const section = FLAG_SETTINGS_GROUPS[groupId];
  if (flagValues.length === 0) return null;

  return (
    <div>
      <div className="mb-3">
        <h3 className="text-sm font-semibold text-[var(--text-sec)]">{section.title}</h3>
        <p className="mt-0.5 text-xs text-[var(--text-ter)]">{section.description}</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {flagValues.map((flagValue) => {
          const keyName = getFlagKeyName(flagValue);
          const config = FLAG_CONFIGS[flagValue];
          if (!config) return null;
          const usage = FEATURE_FLAG_USAGE[flagValue];

          return (
            <FlagRow
              key={flagValue}
              name={config.name}
              description={usage?.description ?? config.description}
              flagKey={config.key}
              enabled={flags[keyName] ?? false}
              usedIn={usage?.file}
              dependencies={config.dependencies}
              onToggle={() => onToggle(keyName)}
              onReset={() => onReset(keyName)}
            />
          );
        })}
      </div>
    </div>
  );
}

const FLAG_CODE =
  'font-mono text-[11px] bg-[var(--hover-tint)] text-[var(--text-sec)] px-1.5 py-0.5 rounded border border-[var(--rule)] dark:bg-[var(--surface)] dark:text-[var(--text-sec)] dark:border-[var(--rule)]';

function getFlagKeyName(flagValue: FeatureFlagValue): string {
  const entry = Object.entries(FEATURE_FLAGS).find(([, v]) => v === flagValue);
  return entry ? entry[0] : flagValue;
}

function errMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  try {
    return JSON.stringify(err);
  } catch {
    return 'Unknown error';
  }
}

interface FlagRowProps {
  name: string;
  description: string;
  flagKey: string;
  enabled: boolean;
  usedIn?: string;
  dependencies?: string[];
  onToggle: () => void;
  onReset: () => void;
}

function FlagRow({
  name,
  description,
  flagKey,
  enabled,
  usedIn,
  dependencies,
  onToggle,
  onReset,
}: FlagRowProps) {
  return (
    <div
      className={` border p-4 transition-colors ${
        enabled ? 'bg-[var(--bg-raised)] border-[var(--rule)]' : 'bg-[var(--surface)] border-[var(--rule)]'
      }`}
    >
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0 mt-0.5 flex flex-col items-center gap-1.5">
          <button
            type="button"
            onClick={onToggle}
            className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1 ${
              enabled ? 'bg-primary-600' : 'bg-[var(--rule-bright)]'
            }`}
            role="switch"
            aria-checked={enabled}
            title={enabled ? `Disable ${name}` : `Enable ${name}`}
          >
            <span
              className={`block w-5 h-5 mx-0.5 my-0.5 bg-[var(--bg-raised)] rounded-full shadow transition-transform ${
                enabled ? 'translate-x-5' : 'translate-x-0'
              }`}
            />
          </button>
          <button
            type="button"
            onClick={onReset}
            className="p-1 text-[var(--text-ter)] hover:text-[var(--text-sec)] rounded"
            title="Reset to default"
          >
            <RefreshCw className="h-3.5 w-3.5" />
          </button>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-0.5">
            <h4 className="text-sm font-semibold text-[var(--text-pri)]">{name}</h4>
            <span
              className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                enabled ? 'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]' : 'bg-[var(--hover-tint)] text-[var(--text-ter)]'
              }`}
            >
              {enabled ? 'On' : 'Off'}
            </span>
          </div>

          <p className="text-sm text-[var(--text-sec)]">{description}</p>

          <div className="mt-3 pt-3 border-t border-[var(--rule)] space-y-1">
            <p className="text-xs text-[var(--text-ter)]">
              Key: <code className={FLAG_CODE}>{flagKey}</code>
            </p>
            {usedIn ? (
              <p className="text-xs text-[var(--text-ter)]">
                Used in: <code className={FLAG_CODE}>{usedIn}</code>
              </p>
            ) : null}
            {dependencies?.length ? (
              <p className="text-xs text-[var(--text-ter)]">
                Depends on: {dependencies.join(', ')}
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function FeatureFlags() {
  const [flags, setFlags] = useState<Record<string, boolean>>({});
  const [loaded, setLoaded] = useState<boolean>(() => areUserFlagsLoaded());
  const [writeError, setWriteError] = useState<string | null>(null);
  const [dismissedModuleKeys, setDismissedModuleKeys] = useState<Set<string>>(() => new Set());
  const userId = useUserStore((s) => s.userId);
  const userRole = useUserStore((s) => s.currentRole);
  const isAdmin =
    userRole === 'workspace-admin' || userRole === 'platform-admin' || userRole === 'system-admin';
  // A team-level module's flag (Team pages) is listed for the Elevation team
  // only, and for nobody until it is known who is signed in.
  const elevationTeam = useIsElevationTeam();
  const workspaceId = getCurrentWorkspaceId();

  const workspaceFeatures = useWorkspaceModules();
  const modulePolicies = useWorkspaceModulePolicies();

  const forcedReenabled = getAdminForcedReenabledFlags(workspaceFeatures, modulePolicies).filter(
    (item) =>
      isFlagListedFor(item.flagKey, elevationTeam) &&
      !dismissedModuleKeys.has(item.moduleKey) &&
      !(
        workspaceId &&
        userId &&
        isAlwaysOnNoticeDismissed(workspaceId, userId, item.moduleKey)
      ),
  );

  const handleDismissForcedNotice = (item: AdminForcedReenabledFlag) => {
    if (workspaceId && userId) {
      dismissAlwaysOnNotice(workspaceId, userId, item.moduleKey);
    }
    setDismissedModuleKeys((prev) => new Set(prev).add(item.moduleKey));
  };

  useEffect(() => {
    if (!userId) return;
    if (areUserFlagsLoaded()) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    loadUserFlags(userId)
      .catch((err) => {
        if (!cancelled) setWriteError(errMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoaded(areUserFlagsLoaded());
      });
    return () => {
      cancelled = true;
    };
  }, [userId]);

  useEffect(() => {
    const recompute = () => {
      const flagStates: Record<string, boolean> = {};
      for (const [key, value] of Object.entries(FEATURE_FLAGS)) {
        const mod = WORKSPACE_MODULES.find((m) => m.flagKey === value);
        // Module-linked flags must show the same effective state as useModuleGate
        // (personal override → else module userDefault). Raw isFeatureEnabled uses
        // FLAG_CONFIGS.defaultEnabled when there is no override, which can disagree
        // with userDefault and make "Off" still render the V2 surface.
        flagStates[key] = mod
          ? resolveModuleGate(workspaceFeatures, modulePolicies, mod.key, isAdmin)
          : isFeatureEnabled(value);
      }
      setFlags(flagStates);
      setLoaded(areUserFlagsLoaded());
    };
    recompute();
    window.addEventListener(FEATURE_FLAGS_UPDATED_EVENT, recompute);
    return () => window.removeEventListener(FEATURE_FLAGS_UPDATED_EVENT, recompute);
  }, [workspaceFeatures, modulePolicies, isAdmin]);

  const runWrite = async (op: () => Promise<void>) => {
    setWriteError(null);
    try {
      await op();
    } catch (err) {
      setWriteError(errMessage(err));
    }
  };

  const handleToggle = (flagKey: string) => {
    const flagValue = FEATURE_FLAGS[flagKey as keyof typeof FEATURE_FLAGS];
    if (!flagValue) return;
    void runWrite(() => (flags[flagKey] ? disableFeature(flagValue) : enableFeature(flagValue)));
  };

  const handleReset = (flagKey: string) => {
    const flagValue = FEATURE_FLAGS[flagKey as keyof typeof FEATURE_FLAGS];
    if (flagValue) void runWrite(() => resetFeature(flagValue));
  };

  const handleEnableAll = () => {
    void runWrite(async () => {
      await Promise.all(visibleFlagValues.map((flag) => enableFeature(flag)));
    });
  };

  const handleDisableAll = () => {
    void runWrite(async () => {
      await Promise.all(visibleFlagValues.map((flag) => disableFeature(flag)));
    });
  };

  const handleResetAll = () => {
    void runWrite(async () => {
      await Promise.all(visibleFlagValues.map((flag) => resetFeature(flag)));
    });
  };

  // Every flag this person may be shown; the counts below are taken from it.
  const allFlagValues = (Object.values(FEATURE_FLAGS) as FeatureFlagValue[]).filter((flag) =>
    isFlagListedFor(flag, elevationTeam),
  );

  // A module-linked flag is a live control only when its workspace module is
  // enabled AND the module's Individual Control policy lets this user override.
  // Flags with no module pairing (behavioral, standalone) are always shown
  // unless explicitly hidden from this page (deprecation / retirement hide).
  const isFlagVisible = (flagValue: FeatureFlagValue): boolean => {
    if (FLAG_CONFIGS[flagValue]?.hideFromFeatureFlagsSettings) return false;
    const mod = WORKSPACE_MODULES.find((m) => m.flagKey === flagValue);
    if (!mod) return true;
    return (
      isModuleEnabled(workspaceFeatures, mod.key) &&
      canUserOverrideModule(modulePolicies, mod.key, isAdmin)
    );
  };

  const visibleFlagValues = allFlagValues.filter(isFlagVisible);
  const hiddenModuleLinkedCount = allFlagValues.length - visibleFlagValues.length;
  const flagsByGroup = groupFlags(visibleFlagValues);

  const visibleKeyNames = new Set(visibleFlagValues.map(getFlagKeyName));
  const enabledCount = Object.entries(flags).filter(
    ([k, v]) => v && visibleKeyNames.has(k),
  ).length;
  const totalCount = visibleFlagValues.length;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<FlaskConical className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="Feature Flags"
        subtitle="Personal toggles stored per user — your settings follow you across devices."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-[var(--text-ter)]">
              {enabledCount} of {totalCount} enabled
            </span>
            <button
              type="button"
              onClick={handleEnableAll}
              className="px-3 py-1.5 text-sm font-medium bg-[var(--green)] text-white hover:bg-[var(--green)]"
            >
              Enable all
            </button>
            <button
              type="button"
              onClick={handleDisableAll}
              className="px-3 py-1.5 text-sm font-medium bg-[var(--coral)] text-white hover:bg-[var(--coral)]"
            >
              Disable all
            </button>
            <button
              type="button"
              onClick={handleResetAll}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)]"
            >
              <RefreshCw className="h-3.5 w-3.5" />
              Reset all
            </button>
          </div>
        }
      />

      {forcedReenabled.length > 0 ? (
        <div className="border border-[var(--mustard)] bg-[var(--mustard-dim)] p-4 dark:border-[var(--mustard)]/60 dark:bg-[var(--mustard)]/20">
          <div className="flex items-start gap-3">
            <ShieldAlert className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5 dark:text-[var(--mustard)]" />
            <div className="flex-1 min-w-0 space-y-2">
              <p className="text-sm font-medium text-[var(--mustard)] dark:text-[var(--mustard)]">
                Required by your workspace administrator
              </p>
              <ul className="space-y-1.5">
                {forcedReenabled.map((item) => (
                  <li
                    key={item.moduleKey}
                    className="flex items-start justify-between gap-3 text-sm text-[var(--mustard)] dark:text-[var(--mustard)]"
                  >
                    <span>
                      <span className="font-medium">{item.featureName}</span>
                      {' '}was re-enabled and is now required by your workspace administrator.
                    </span>
                    <button
                      type="button"
                      onClick={() => handleDismissForcedNotice(item)}
                      className="flex-shrink-0 rounded p-0.5 text-[var(--mustard)] hover:bg-[var(--mustard-dim)] hover:text-[var(--mustard)] dark:text-[var(--mustard)] dark:hover:bg-[var(--mustard)]/40 dark:hover:text-[var(--mustard)]"
                      aria-label={`Dismiss notice for ${item.featureName}`}
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      ) : null}

      {writeError ? (
        <div className="card bg-[var(--coral-dim)] border-[var(--coral)]">
          <div className="flex items-start gap-2">
            <XCircle className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-[var(--coral)]">Failed to save flag change</p>
              <p className="text-sm text-[var(--coral)] mt-0.5">
                Your local toggle updated, but the database write failed.
              </p>
              <p className="text-xs text-[var(--coral)] font-mono mt-1">{writeError}</p>
            </div>
            <button
              type="button"
              onClick={() => setWriteError(null)}
              className="text-[var(--coral)] hover:text-[var(--coral)]"
              aria-label="Dismiss"
            >
              <XCircle className="h-4 w-4" />
            </button>
          </div>
        </div>
      ) : null}

      {!loaded ? (
        <div className="card">
          <div className="text-sm text-[var(--text-ter)] py-4 text-center">Loading feature flag settings…</div>
        </div>
      ) : visibleFlagValues.length === 0 ? (
        <div className="card text-sm text-[var(--text-ter)] text-center py-6">
          No feature flags are available for your account.
        </div>
      ) : (
        <div className={`space-y-8 ${loaded ? '' : 'opacity-50 pointer-events-none'}`}>
          {FLAG_SETTINGS_GROUP_ORDER.map((groupId) => (
            <FlagGroupSection
              key={groupId}
              groupId={groupId}
              flagValues={flagsByGroup[groupId] ?? []}
              flags={flags}
              onToggle={handleToggle}
              onReset={handleReset}
            />
          ))}
        </div>
      )}

      {hiddenModuleLinkedCount > 0 ? (
        <p className="text-xs text-[var(--text-ter)]">
          {hiddenModuleLinkedCount} module-linked flag{hiddenModuleLinkedCount === 1 ? '' : 's'} hidden
          — their workspace modules are disabled or locked here. Workspace admins manage these under
          Settings → Modules.
        </p>
      ) : null}

      <div className="card bg-[var(--slate-dim)] border-[var(--slate)] space-y-2">
        <p className="text-sm font-medium text-[var(--text-sec)]">How feature flags work</p>
        <p className="text-sm text-[var(--text-sec)]">
          <strong>Personal override</strong> — changes are stored per user in Supabase and follow you
          across devices. Other workspace members are unaffected.
        </p>
        <p className="text-sm text-[var(--text-sec)]">
          <strong>Module linkage</strong> — for flags tied to a workspace module, the module must also
          be enabled and your Individual Control policy must allow override (see Settings → Modules).
        </p>
        <p className="text-sm text-[var(--text-sec)]">
          <strong>Reset</strong> clears your override so the flag falls back to the
          module&apos;s member default (Settings → Modules → Individual Control), not
          only the registry default. Layout and navigation changes may need a page refresh.
        </p>
      </div>
    </div>
  );
}

export default FeatureFlags;
